import { useState } from "react";
import { Divider } from "./Chrome";
import { Reveal } from "./lib";
import { Assured, CellBody, CsvButton, DataTable, type Table } from "./DataTable";
import raw from "./disclosures.json";

type Section = { id: string; title: string; intro?: string[]; tables: Table[]; notes: string[] };
const data = raw as unknown as { lead: string; more: string; legend: string; sections: Section[] };

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

function csvName(s: Section, t: Table, i: number) {
  const group = t.rows.find((r) => r[0]?.g)?.[0]?.t;
  return s.tables.length > 1 && group ? `${s.id}-${slug(group)}` : s.tables.length > 1 ? `${s.id}-${i + 1}` : s.id;
}

function Tcfd({ table }: { table: Table }) {
  return (
    <div className="border-t border-[#c9cbcd]" data-testid="am-tcfd">
      <div className="hidden grid-cols-[300px_1fr] bg-[var(--am-yellow)] text-sm font-semibold text-[#2b2d30] md:grid">
        <span className="px-4 py-2.5 text-center">Topic</span><span className="px-4 py-2.5 text-center">Response and References</span>
      </div>
      {table.rows.map((row, i) => row[0]?.g ? (
        <h4 key={i} className="mt-6 bg-[var(--am-blue)] px-4 py-2.5 text-sm font-semibold text-white first:mt-0 md:mt-0">{row[0].t}</h4>
      ) : (
        <div key={i} className="grid gap-2 border-b border-[#c9cbcd] py-4 text-sm leading-relaxed md:grid-cols-[300px_1fr] md:gap-0 md:py-0">
          <p className="font-semibold text-[#2b2d30] md:border-r md:border-[#c9cbcd] md:px-4 md:py-4">{row[0]?.t}</p>
          <div className="min-w-0 md:px-4 md:py-4"><CellBody cell={row[1] ?? { t: "" }} /></div>
        </div>
      ))}
    </div>
  );
}

function Panel({ s }: { s: Section }) {
  return (
    <div className="space-y-8" data-testid={`am-data-panel-${s.id}`}>
      <h3 className="am-title text-3xl sm:text-4xl lg:text-5xl">{s.title}</h3>
      {s.intro?.map((p, i) => <p key={i} className={`max-w-5xl text-[15px] leading-relaxed ${i === 0 ? "font-semibold text-[#2b2d30]" : ""}`}>{p}</p>)}
      {s.id === "tcfd-index"
        ? <div><div className="mb-3 flex justify-end"><CsvButton table={s.tables[0]} name={s.id} testId="am-data-tcfd-index-csv" /></div><Tcfd table={s.tables[0]} /></div>
        : <div className="grid gap-10 lg:grid-cols-2 [&>*]:min-w-0">
            {s.tables.map((t, i) => (
              <div key={i} className={t.head.length > 2 ? "lg:col-span-2" : ""}>
                <div className="mb-3 flex justify-end"><CsvButton table={t} name={csvName(s, t, i)} testId={`am-data-${s.id}-table-${i}-csv`} /></div>
                <DataTable table={t} testId={`am-data-${s.id}-table-${i}`} />
              </div>
            ))}
          </div>}
      {s.notes.length > 0 && (
        <ul className="space-y-1.5 border-t border-[var(--am-line)] pt-4 text-xs leading-relaxed" data-testid={`am-data-${s.id}-notes`}>
          {s.notes.map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}
    </div>
  );
}

export function DisclosuresSection() {
  const [active, setActive] = useState(data.sections[0].id);
  const current = data.sections.find((s) => s.id === active) ?? data.sections[0];
  return (
    <section id="data" className="am-section scroll-mt-28" data-testid="am-section-data">
      <Divider id="data" number="06" title="Data and Disclosures" items={data.sections.map((s) => s.title)}
        image="p62_0.jpg" alt="A woman reading a tablet at dusk, overlaid with a grid of data points" />
      <div className="mx-auto max-w-[1320px] px-5 py-20 sm:px-8 sm:py-28">
        <Reveal>
          <p className="am-headline max-w-5xl text-xl sm:text-2xl lg:text-3xl" data-testid="am-data-lead">{data.lead}</p>
          <p className="mt-4 text-[15px]">{data.more}</p>
        </Reveal>
        <div role="tablist" aria-label="Data and Disclosures" className="am-scroll-x sticky top-[var(--am-head,146px)] z-20 -mx-5 mt-14 flex gap-2 overflow-x-auto bg-white/95 px-5 py-3 backdrop-blur sm:-mx-8 sm:px-8">
          {data.sections.map((s) => (
            <button key={s.id} type="button" role="tab" aria-selected={active === s.id} onClick={(e) => { setActive(s.id); e.currentTarget.scrollIntoView({ inline: "nearest", block: "nearest" }); }} data-testid={`am-data-tab-${s.id}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${active === s.id ? "border-[var(--am-blue)] bg-[var(--am-blue)] text-white" : "border-[var(--am-line)] text-[var(--am-gray)] hover:border-[var(--am-blue)] hover:text-[var(--am-blue)]"}`}>
              {s.title}
            </button>
          ))}
        </div>
        <p className="mt-6 text-xs" data-testid="am-data-legend"><Assured /> {data.legend}</p>
        <div role="tabpanel" className="mt-8"><Panel key={current.id} s={current} /></div>
      </div>
    </section>
  );
}
